/**
 * 页面真实控件到稳定 data-guide 锚点的集中映射。
 *
 * 映射仅使用项目自己维护的语义 class、原生元素、可访问角色和固定业务文案；
 * 不依赖 Element UI 自动生成 class、表格行号或某条业务数据的内容。
 */

const bySelector = (anchor, selector, options = {}) => ({anchor, selector, ...options})
const byText = (anchor, text, options = {}) => ({anchor, selector: options.selector || 'button', text, ...options})
const byTexts = (anchor, texts, options = {}) => ({anchor, selector: options.selector || 'button', texts, ...options})

const ROUTE_ANCHOR_RECIPES = Object.freeze({
  'admin-dashboard': [
    byText('dashboard-todo-queue', '待办队列', {selector: '.section-card__title'}),
    bySelector('dashboard-workflow-map', '.workflow-navigator'),
    byText('dashboard-risk-alert', '风险提醒', {selector: '.insight-panel__title'}),
  ],
  'admin-notices': [
    byText('notices-unread-filter', '阅读状态', {selector: 'label'}),
    byText('notices-open-message', '查看', {all: true}),
    byTexts('notices-read-all', ['全部已读', '处理中…']),
  ],
  'admin-personal-calendar': [
    bySelector('personal-calendar-view-switch', '.personal-calendar-view-switch'),
    bySelector('personal-calendar-create', '.personal-calendar-create'),
    bySelector('personal-calendar-day-list', '.personal-calendar-day-dialog__content'),
  ],
  'admin-leads': [
    bySelector('leads-owner-status', 'button[aria-label="查看待分配线索"]'),
    byText('leads-assign-action', '指派', {all: true}),
    byText('leads-recycle-action', '回收', {all: true}),
    byText('leads-owner-result', '负责人', {selector: 'th'}),
  ],
  'admin-my-leads': [
    bySelector('my-leads-search', 'input[placeholder="搜索联系人 / 企业名称"]'),
    byText('my-leads-next-action', '下一步动作', {selector: 'th'}),
    byText('my-leads-follow-action', '写跟进', {all: true}),
    byText('my-leads-convert-action', '转商机', {all: true}),
    byText('my-leads-close-action', '放弃', {all: true}),
  ],
  'admin-opportunities': [
    bySelector('opportunities-amount-summary', '.opportunity-amount-summary'),
    byText('opportunities-create', '新增商机'),
    bySelector('opportunities-owner-edit', '[data-guide="opportunities-owner-edit"]'),
    byText('opportunities-follow-action', '记录跟进', {all: true}),
    byText('opportunities-next-step', '下一步动作', {selector: 'th'}),
  ],
  'admin-customers': [
    bySelector('customers-search', 'input[placeholder="搜索客户 / 信用代码 / 主营业务 / 地址 / 手机号"]'),
    byText('customers-add', '新增客户'),
    byTexts('customers-contact-gap', ['补充联系人', '绑定手机号'], {all: true}),
  ],
  'admin-partners': [
    byText('partners-create', '新增合作生态'),
    byText('partners-member-link', '生态成员', {all: true}),
    byText('partners-status-result', '合作状态', {selector: 'th'}),
  ],
  'admin-products': [
    byText('products-create', '新增服务项'),
    byText('products-publish-setting', '发布状态', {selector: 'label'}),
    byText('products-price-result', '参考单价', {selector: 'th'}),
  ],
  'admin-quotations': [
    byText('quotations-create', '新增报价'),
    bySelector('quotations-lines', '.quotation-form-section--details'),
    byTexts('quotations-approval-action', ['提交审批', '撤销提交审批', '通过', '驳回', '转合同'], {all: true}),
    byText('quotations-total-result', '报价金额', {selector: 'th'}),
  ],
  'admin-quotation-detail': [
    byText('quotation-detail-lines', '明细名称', {selector: 'th'}),
    byText('quotation-detail-summary', '报价金额', {selector: '.quotation-detail-summary > span'}),
  ],
  'admin-contracts': [
    byText('contracts-status-filter', '合同状态', {selector: 'label'}),
    byText('contracts-signing-action', '维护签署', {all: true}),
    byText('contracts-project-result', '生成项目', {selector: 'th'}),
    byText('contracts-batch-import', '批量导入'),
    bySelector('contracts-batch-import-preview', '.contract-import-preview'),
    byText('contracts-batch-import-submit', '确认导入'),
  ],
  'admin-contract-detail': [
    bySelector('contract-signing-materials', '.contract-detail-materials'),
    byText('contract-confirm-signed', '确认已线下签署'),
    byText('contract-launch-project', '生成项目'),
    byText('contract-historical-settlement-entry', '历史结算情况'),
    byText('contract-historical-settlement-files', '选择到账凭证'),
    byText('contract-legacy-invoice-upload', '补传历史发票'),
    bySelector('contract-legacy-invoice-summary', '.contract-legacy-invoice-summary'),
    byText('contract-legacy-invoice-files', '选择发票附件'),
  ],
  'admin-projects': [
    byText('projects-bottleneck', '项目阶段', {selector: 'label'}),
    byText('projects-self-action', '自营执行', {all: true}),
    byText('projects-channel-action', '开放接单', {all: true}),
  ],
  'admin-project-detail': [
    byText('project-profile-tab', '项目主档', {selector: '[role="tab"]'}),
    byText('project-resource-tab', '资源履约', {selector: '[role="tab"]'}),
    byTexts('project-acceptance-actions', ['提醒客户', '撤回发起', '线下验收补录'], {all: true}),
    byText('project-acceptance-history', '内部处理记录', {selector: '.section-card__title'}),
  ],
  'admin-acceptance': [
    byText('acceptance-status-filter', '验收状态', {selector: 'label'}),
    byText('acceptance-view-action', '查看验收', {all: true}),
    byText('acceptance-next-action', '下一步动作', {selector: 'th'}),
    byText('acceptance-manual-confirm', '线下验收补录'),
    byText('acceptance-internal-remind', '提醒客户', {all: true}),
    byText('acceptance-internal-more', '更多', {all: true}),
  ],
  'admin-project-pool': [
    byText('project-pool-open', '开放项目'),
    byText('project-pool-scope', '选择范围'),
    byText('project-pool-status-result', '状态', {selector: 'th'}),
  ],
  'admin-partner-applications': [
    byText('applications-status-filter', '审核状态', {selector: 'label'}),
    byTexts('applications-review-action', ['通过', '驳回'], {all: true}),
    byText('applications-task-code-action', '生成任务码', {all: true}),
  ],
  'admin-flight-tasks': [
    byText('flight-tasks-submitted-filter', '执行状态', {selector: 'label'}),
    byTexts('flight-tasks-review-action', ['审核通过', '退回补充'], {all: true}),
    byText('flight-tasks-task-code', '任务码', {selector: 'th'}),
  ],
  'admin-flight-task-calendar': [
    bySelector('calendar-month-nav', '.calendar-toolbar'),
    bySelector('calendar-unscheduled', '.calendar-summary-card--warning'),
    byText('calendar-day-section', '当日作业安排', {selector: '.section-card__title'}),
  ],
  'admin-flight-pilot-assessments': [
    byText('assessments-filter', '综合等级', {selector: 'label'}),
    byText('assessments-score', '四维均分', {selector: 'th'}),
    byText('assessments-open-detail', '查看历史', {all: true}),
  ],
  'admin-flight-task-detail': [
    bySelector('flight-detail-task-code', '.task-code-panel'),
    byText('flight-detail-regenerate-code', '重新生成任务码'),
    byText('flight-detail-evidence-tabs', '履约记录', {selector: '[role="tab"]'}),
    byTexts('flight-detail-review-action', ['审核通过', '退回补充'], {all: true}),
  ],
  'admin-flight-pilots': [
    byText('members-admission-filter', '成员准入审核', {selector: 'label'}),
    byText('members-role-result', '岗位标签', {selector: 'th'}),
    byText('members-admission-action', '准入审核', {all: true}),
  ],
  'admin-operation-standards': [
    byText('standards-create', '新增作业类型'),
    bySelector('standards-bindings', '.binding-section'),
    byText('standards-preview', '任务生成预览', {selector: 'h3'}),
  ],
  'admin-operation-standard-bundles': [
    byText('bundles-add-material', '新增素材'),
    byText('bundles-create-set', '新增集合'),
    byText('bundles-preview-check', '预览检查', {selector: 'th'}),
  ],
  'admin-learning-records': [
    byText('learning-status-filter', '学习状态', {selector: 'label'}),
    byText('learning-open-detail', '查看', {all: true}),
    byText('learning-export', '导出当前筛选'),
  ],
  'admin-media-assets': [
    byText('media-customer-filter', '客户可见', {selector: 'label'}),
    byText('media-open-detail', '查看资料', {all: true}),
    byTexts('media-customer-tab', ['查看客户可见内容', '客户实际可见'], {selector: 'button, [role="tab"]', all: true}),
  ],
  'admin-payment-plans': [
    byText('payment-plans-status-filter', '状态', {selector: 'label'}),
    byText('payment-plans-create', '新建合同计划'),
    bySelector('payment-plans-lines', '.plan-editor-table input'),
  ],
  'admin-payments': [
    byText('payments-status-filter', '状态', {selector: 'label'}),
    byText('payments-confirm', '确认到账'),
    byText('payments-void-action', '作废', {all: true}),
  ],
  'admin-invoices': [
    byText('invoices-status-filter', '状态', {selector: 'label'}),
    byText('invoices-upload-action', '上传发票', {all: true}),
    byText('invoices-issue-form', '发票号码', {selector: 'label'}),
    byText('invoices-detail-action', '详情', {all: true}),
    bySelector('invoices-detail-attachments', '[data-guide="invoice-detail-attachments"]'),
  ],
  'admin-reconciliation': [
    byText('reconciliation-filters', '日期范围', {selector: 'label'}),
    byText('reconciliation-export', '导出正式 XLSX'),
    byText('reconciliation-paid-summary', '已到账', {selector: '.summary-item span'}),
  ],
  'admin-feedback-tickets': [
    byText('feedback-status-filter', '处理状态', {selector: 'label'}),
    byTexts('feedback-reply-action', ['开始处理', '回复'], {all: true}),
    bySelector('feedback-reply-form', 'textarea[placeholder="请填写提交人可见的处理结果或下一步说明"]'),
  ],
  'admin-work-reports': [
    bySelector('work-reports-tabs', '.work-report-tabs'),
    byText('work-reports-submit', '确认提交'),
    byTexts('work-reports-review', ['已阅', '发送'], {all: true}),
  ],
  'admin-notice-templates': [
    bySelector('notice-templates-search', 'input[placeholder="模板名称或事件编码"]'),
    byText('notice-templates-event', '事件', {selector: 'th'}),
    byText('notice-templates-edit-action', '编辑', {all: true}),
  ],
  'admin-notice-logs': [
    byText('notice-logs-failure-filter', '状态', {selector: 'label'}),
    byText('notice-logs-retry-action', '重新发送', {all: true}),
    byText('notice-logs-result', '发送结果', {selector: 'th'}),
  ],
  'admin-report-exports': [
    byText('report-type-select', '报表类型', {selector: 'label'}),
    byText('report-export-action', '导出', {all: true}),
    byText('report-scope-column', '筛选口径', {selector: 'th'}),
  ],
  'admin-case-library': [
    bySelector('website-tabs', '.website-content__tree-card'),
    byText('website-save-action', '保存草稿'),
    byTexts('website-publish-action', ['发布到官网', '发布', '下架'], {all: true}),
  ],
  'admin-system-accounts': [
    bySelector('accounts-search', 'input[placeholder="搜索成员姓名、账号或手机号"]'),
    byText('accounts-create', '新增成员'),
    byText('accounts-role-scope', '职责角色', {selector: 'label'}),
  ],
  'admin-system-miniapp-users': [
    bySelector('miniapp-users-search', 'input[placeholder="用户编号 / 昵称 / 姓名 / 手机号 / 合作生态"]'),
    byText('miniapp-users-role-action', '关联角色', {all: true}),
    byText('miniapp-users-invite', '更多', {all: true}),
  ],
  'admin-system-roles': [
    byText('roles-summary', '权限摘要', {selector: 'th'}),
    byText('roles-copy-action', '复制', {all: true}),
    bySelector('roles-data-scope', '.role-scope-table input[placeholder="不配置"]'),
  ],
  'admin-system-menus': [
    byText('menus-entry-column', '功能入口', {selector: 'th'}),
    byText('menus-edit-action', '编辑', {all: true}),
    byText('menus-status-column', '状态', {selector: 'th'}),
  ],
  'admin-system-dictionaries': [
    bySelector('dictionaries-group', '.dictionary-card__header h3'),
    byText('dictionaries-edit-action', '编辑', {all: true}),
    byText('dictionaries-items', '新增字段值'),
  ],
  'admin-system-config': [
    byText('system-config-payee-impact', '客户打款方式', {selector: '.finance-payee-panel__head strong'}),
    bySelector('system-config-values', '.finance-payee-field input'),
    byText('system-config-save', '保存全部配置'),
  ],
  'admin-system-resource-types': [
    byText('resource-types-create', '新增资源项'),
    byText('resource-types-flow', '默认后续流转', {selector: 'label'}),
    byText('resource-types-units', '计量单位列表', {selector: 'th'}),
  ],
  'admin-system-flight-task-milestones': [
    byText('milestones-create', '新增模板'),
    bySelector('milestones-node-types', '.milestone-template-editor input[placeholder="节点类型"]'),
    byText('milestones-nodes-result', '节点', {selector: 'th'}),
  ],
  'admin-operation-logs': [
    byText('operation-logs-filters', '业务对象', {selector: 'label'}),
    byTexts('operation-logs-detail', ['完整字段', '精简字段']),
    byText('operation-logs-action-column', '动作', {selector: 'th'}),
  ],
  'admin-system-environment': [
    byText('environment-refresh', '刷新环境状态'),
    byText('environment-port', '后端端口', {selector: 'td, th'}),
    byText('environment-summary', '启用环境', {selector: 'td, th'}),
  ],
  login: [
    bySelector('auth-login-fields', '.login-card input[placeholder="请输入手机号或用户名"]'),
    bySelector('auth-login-submit', '.login-card__submit'),
  ],
  'change-password': [
    bySelector('auth-password-fields', '.password-card input[autocomplete="current-password"]'),
    bySelector('auth-password-submit', '.password-card__submit'),
  ],
})

module.exports = {ROUTE_ANCHOR_RECIPES}
